import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Users, FloppyDisk, Trash, PaperPlaneTilt, Lightning } from "@phosphor-icons/react";

const C = { navy: "#0B1E3B", red: "#E01A2E", cream: "#EFECE4", body: "#F7F5F0", slate: "#5B6B7E", green: "#1E9E63", amber: "#F5A623", ink: "#1A1E24" };
const blank = { product: "", insurer: "", has_app: "", activated_app: "", member_type: "", exclude_opt_out: true };

const toFilter = (f) => ({
  product: f.product || null, insurer: f.insurer || null,
  has_app: f.has_app === "" ? null : f.has_app === "true",
  activated_app: f.activated_app === "" ? null : f.activated_app === "true",
  member_type: f.member_type || null, exclude_opt_out: f.exclude_opt_out,
});

const PRESETS = [
  { label: "App downloaders — all", f: { ...blank, has_app: "true" } },
  { label: "Not onboarded — Indwe", f: { ...blank, insurer: "Indwe", has_app: "false" } },
  { label: "Consumer main members", f: { ...blank, product: "consumer", member_type: "main" } },
];

export default function CommunicationsPage() {
  const [insurers, setInsurers] = useState([]);
  const [f, setF] = useState(blank);
  const [preview, setPreview] = useState({ count: 0, sample: [] });
  const [name, setName] = useState("");
  const [type, setType] = useState("dynamic");
  const [segments, setSegments] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [tmplId, setTmplId] = useState("");
  const [nt, setNt] = useState({ name: "", subject: "", body: "" });
  const [showNew, setShowNew] = useState(false);
  const [bcastSeg, setBcastSeg] = useState("");
  const [bcastPreview, setBcastPreview] = useState(null);
  const [groupByPolicy, setGroupByPolicy] = useState(false);
  const [migration, setMigration] = useState(null);
  const [audit, setAudit] = useState(null);
  const [waves, setWaves] = useState([]);
  const [autoChase, setAutoChase] = useState(null);
  const [waveEmails, setWaveEmails] = useState("genevieve@resq1.co.za");
  const [migLoading, setMigLoading] = useState(false);
  const [migSending, setMigSending] = useState(false);

  // Debit order authority — issue a re-mandate request to an audience of EXISTING records.
  const [doScope, setDoScope] = useState("app");
  const [doPartner, setDoPartner] = useState("");
  const [partners, setPartners] = useState([]);
  const [doPrev, setDoPrev] = useState(null);
  const [doBusy, setDoBusy] = useState(false);
  useEffect(() => { api.get("/admin/partners").then((r) => setPartners(r.data.partners || [])).catch(() => {}); }, []);
  const doBody = (confirm) => ({ scope: doScope, partner: doPartner || null, exclude_indwe: true, confirm });
  const previewDO = async () => {
    setDoBusy(true);
    try { const r = await api.post("/admin/debit-order/request", doBody(false)); setDoPrev(r.data); }
    catch (e) { toast.error("Preview failed", { description: e.response?.data?.detail || e.message }); }
    finally { setDoBusy(false); }
  };
  const sendDO = async () => {
    if (!doPrev) return;
    const n = (doPrev.will_email || 0) + (doPrev.will_banner || 0);
    if (!window.confirm(`Issue a debit order authority request to ${doPrev.will_email || 0} by email and prompt ${doPrev.will_banner || 0} app users? (${n} people). Indwe clients are never emailed.`)) return;
    setDoBusy(true);
    try {
      const r = await api.post("/admin/debit-order/request", doBody(true));
      toast.success("Requests issued", { description: `${r.data.emailed || 0} emailed · ${r.data.bannered || 0} app prompts set` });
      setDoPrev(r.data);
    } catch (e) { toast.error("Send failed", { description: e.response?.data?.detail || e.message }); }
    finally { setDoBusy(false); }
  };

  // One shared duplicate gate: preview tells us who has had this mail before, and nothing is
  // re-mailed unless the operator types the override.
  const dupeOk = (d, what) => {
    if (!d?.duplicates) return true;
    const conf = d.duplicates_confirmed_delivered
      ? `\n\n${d.duplicates_confirmed_delivered} of them were CONFIRMED DELIVERED by Resend.` : "";
    return window.confirm(
      `DUPLICATE WARNING\n\n${d.duplicates} of these people have already had this mail in the last 30 days.${conf}`
      + `\n\nThey have been left out of this ${what}. Press OK to send to the rest only.`);
  };

  const loadTemplates = () => api.get("/mail-templates?approved_only=true").then((r) => setTemplates(r.data));
  const previewBroadcast = async () => {
    if (!tmplId) return toast.error("Pick a template first");
    if (!bcastSeg) return toast.error("Pick a saved segment first");
    try {
      const r = await api.post("/communications/broadcast", { template_id: tmplId, segment_id: bcastSeg, group_by_policy: groupByPolicy, dry_run: true });
      setBcastPreview(r.data);
      if (r.data.duplicates) toast.warning(r.data.duplicate_warning, { duration: 12000 });
    }
    catch (e) { toast.error("Preview failed", { description: e.response?.data?.detail || e.message }); }
  };
  const sendBroadcast = async () => {
    if (!bcastPreview) return;
    const warn = bcastPreview.prod_email_enabled ? "" : " PROD_EMAIL_ENABLED is OFF — only allowlisted addresses will actually receive it.";
    if (!dupeOk(bcastPreview, "broadcast")) return;
    if (!window.confirm(`Send this email to ${bcastPreview.recipients} recipients?${warn}`)) return;
    try {
      const r = await api.post("/communications/broadcast", { template_id: tmplId, segment_id: bcastSeg, group_by_policy: groupByPolicy, dry_run: false });
      const d = r.data;
      toast.success("Broadcast processed", { description: `${d.counts?.sent || 0} sent · ${d.counts?.blocked || 0} blocked · ${d.counts?.simulated || 0} simulated` });
      setBcastPreview(null);
    } catch (e) { toast.error("Send failed", { description: e.response?.data?.detail || e.message }); }
  };
  const sendTest = async () => {
    if (!tmplId) return toast.error("Pick an approved template first");
    try { const r = await api.post("/mail-templates/test", { template_id: tmplId }); toast.success(`Test email sent to ${r.data.sent_to}`); }
    catch (e) { toast.error("Test send failed", { description: e.response?.data?.detail || e.message }); }
  };
  const saveTemplate = async () => {
    if (!nt.name.trim() || !nt.subject.trim()) return toast.error("Name and subject are required");
    try {
      const r = await api.post("/mail-templates", nt);
      await api.post(`/mail-templates/${r.data.id}/approve`);
      toast.success("Template saved & approved for reuse");
      setNt({ name: "", subject: "", body: "" }); setShowNew(false); loadTemplates();
    } catch (e) { toast.error("Save failed", { description: e.response?.data?.detail || e.message }); }
  };

  const [edit, setEdit] = useState(null);
  const saveEdit = async () => {
    if (!edit.subject.trim() || !edit.body.trim()) return toast.error("Subject and body are required");
    try {
      await api.patch(`/mail-templates/${edit.id}`, { subject: edit.subject, body: edit.body });
      toast.success("Wording saved — this is what members will receive from now on");
      setEdit(null); loadTemplates();
    } catch (e) { toast.error("Save failed", { description: e.response?.data?.detail || e.message }); }
  };
  const deleteTemplate = async (t) => {
    if (!window.confirm(`Delete the template "${t.name}"? Any flow that uses it falls back to the built-in wording.`)) return;
    try { await api.delete(`/mail-templates/${t.id}`); toast.success("Template deleted"); setTmplId(""); loadTemplates(); }
    catch (e) { toast.error("Delete failed", { description: e.response?.data?.detail || e.message }); }
  };

  const loadSegments = () => api.get("/segments").then((r) => setSegments(r.data));
  useEffect(() => {
    api.get("/contacts/stats").then((r) => setInsurers(r.data.by_insurer.map((b) => b.insurer)));
    loadSegments();
    loadTemplates();
  }, []);

  const runPreview = useCallback((flt) => {
    api.post("/segments/preview", toFilter(flt)).then((r) => setPreview(r.data)).catch((e) => console.error("segment preview failed", e));
  }, []);
  useEffect(() => { runPreview(f); }, [f, runPreview]);

  const save = async () => {
    if (!name.trim()) return toast.error("Give the segment a name");
    try {
      await api.post("/segments", { name: name.trim(), type, filter: toFilter(f) });
      toast.success(`Segment saved · ${preview.count.toLocaleString()} recipients`);
      setName(""); loadSegments();
    } catch (e) { toast.error("Save failed", { description: e.response?.data?.detail || e.message }); }
  };
  const del = async (id) => { await api.delete(`/segments/${id}`); toast.success("Segment removed"); loadSegments(); };

  const loadMigration = async () => {
    setMigLoading(true);
    try {
      const [p, a, w, ac] = await Promise.all([
        api.get("/communications/migration/preflight"),
        api.get("/communications/migration/audit"),
        api.get("/communications/migration/waves"),
        api.get("/communications/migration/auto-chase"),
      ]);
      setMigration(p.data); setAudit(a.data); setWaves(w.data.waves || []); setAutoChase(ac.data);
    }
    catch (e) { toast.error("Pre-flight failed", { description: e.response?.data?.detail || e.message }); }
    finally { setMigLoading(false); }
  };
  const sendMigration = async (opts = {}) => {
    if (!migration) return;
    const first = Boolean(opts.only_emails);
    const prev = Boolean(opts.preview_to);
    const warn = prev
      ? `Email one sample copy to ${opts.preview_to}?`
      : first
      ? `Send the first wave to: ${opts.only_emails.join(", ")}?`
      : migration.prod_email_enabled
        ? `This will email the remaining main members for real${opts.exclude_previous ? " (skipping anyone already sent)" : ""}. Continue?`
        : `PROD_EMAIL_ENABLED is OFF — this will only reach the test allowlist (${(migration.allowlist || []).join(", ")}). Continue with the test send?`;
    if (!prev) {
      let check;
      try {
        check = (await api.post("/communications/migration/send", { dry_run: true, ...opts })).data;
      } catch (e) { return toast.error("Couldn't check that audience", { description: e.response?.data?.detail || e.message }); }
      if (!check.recipients) return toast.error(check.duplicate_warning || "Nobody left to send to");
      if (!dupeOk(check, "send")) return;
    }
    if (!window.confirm(warn)) return;
    setMigSending(true);
    try {
      const r = await api.post("/communications/migration/send", { dry_run: false, ...opts });
      const d = r.data;
      toast.success(prev ? "Preview sent" : first ? "First wave sent" : "Migration broadcast processed", { description: `${d.counts?.sent || 0} sent · ${d.counts?.blocked || 0} blocked · ${d.counts?.simulated || 0} simulated · ${d.counts?.failed || 0} failed` });
      loadMigration();
    } catch (e) { toast.error("Migration send failed", { description: e.response?.data?.detail || e.message }); }
    finally { setMigSending(false); }
  };

  const toggleAutoChase = async () => {
    try {
      const { data } = await api.post("/communications/migration/auto-chase", { enabled: !autoChase.enabled });
      setAutoChase({ ...autoChase, enabled: data.enabled });
      toast.success(data.enabled ? "Auto-chase on — stragglers get reminded at 3 and 7 days" : "Auto-chase off");
    } catch (e) { toast.error("Couldn't change that", { description: e.response?.data?.detail || e.message }); }
  };

  const chaseWave = async (w) => {
    if (!w.chaseable) return toast.error("Nobody in this wave has an email address to chase");
    let check;
    try {
      check = (await api.post(`/communications/migration/waves/${w.id}/chase`, { dry_run: true })).data;
    } catch (e) { return toast.error("Couldn't check that wave", { description: e.response?.data?.detail || e.message }); }
    if (!check.to_chase) return toast.error(check.duplicate_warning || "Nobody left to chase in this wave");
    if (!dupeOk(check, "reminder")) return;
    if (!window.confirm(`Send the reminder to ${check.to_chase} member${check.to_chase === 1 ? "" : "s"} who haven't come back yet? Anyone who has already activated, already had this reminder, bounced or opted out is left out.`)) return;
    setMigSending(true);
    try {
      const { data } = await api.post(`/communications/migration/waves/${w.id}/chase`, { dry_run: false });
      toast.success("Chase sent", { description: `${data.counts?.sent || 0} sent · ${data.counts?.failed || 0} failed${data.no_email ? ` · ${data.no_email} had no email` : ""}${data.duplicates ? ` · ${data.duplicates} skipped as duplicates` : ""}` });
      loadMigration();
    } catch (e) { toast.error("Chase failed", { description: e.response?.data?.detail || e.message }); }
    finally { setMigSending(false); }
  };

  const set = (k, v) => setF({ ...f, [k]: v });

  return (
    <div style={{ background: C.body, minHeight: "100vh", padding: 32 }} data-testid="communications-page">
      <div style={{ fontSize: 10, letterSpacing: "0.16em", textTransform: "uppercase", color: C.slate, fontFamily: "'JetBrains Mono', monospace", display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ width: 22, height: 1, background: C.red }} /> Outbound comms
      </div>
      <h1 style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: 34, fontWeight: 500, letterSpacing: "-0.02em", color: C.ink, margin: "6px 0 6px" }}>Communications</h1>
      <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", background: `${C.amber}18`, borderRadius: 8, fontSize: 12, color: C.ink, marginBottom: 20, maxWidth: 620 }}>
        <PaperPlaneTilt size={14} color={C.amber} /> Email channel · run the pre-flight below to confirm the send gate and data readiness before any broadcast.
      </div>

      <div style={{ background: "#fff", border: `1px solid ${C.red}33`, borderRadius: 12, padding: 22, marginBottom: 20 }} data-testid="do-card">
        <div style={{ fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", color: C.red, fontFamily: "'JetBrains Mono', monospace", marginBottom: 6 }}>Debit order authority</div>
        <div style={{ fontSize: 13, color: C.slate, lineHeight: 1.55, marginBottom: 14, maxWidth: 680 }}>
          Ask existing clients for a fresh debit order authority — <b>no re-sign-up, nothing is deleted</b>. App users
          get an in-app prompt; everyone else is emailed a secure link. <b>Indwe clients are never emailed</b> (they
          get a farewell mail later), but Indwe clients who have the app still get the prompt. Nothing sends until you press Send.
        </div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", marginBottom: 14 }}>
          <select value={doScope} onChange={(e) => { setDoScope(e.target.value); setDoPrev(null); }} data-testid="do-scope"
            style={{ padding: "9px 12px", border: "1px solid rgba(26,30,36,0.16)", borderRadius: 8, fontSize: 13, background: "#fff", color: C.ink }}>
            <option value="app">Clients who downloaded the app</option>
            <option value="active">Everyone marked active</option>
            <option value="all">Everyone (match partner below)</option>
          </select>
          <select value={doPartner} onChange={(e) => { setDoPartner(e.target.value); setDoPrev(null); }} data-testid="do-partner"
            style={{ padding: "9px 12px", border: "1px solid rgba(26,30,36,0.16)", borderRadius: 8, fontSize: 13, background: "#fff", color: C.ink }}>
            <option value="">All partners</option>
            {partners.map((p) => <option key={p.name} value={p.name}>{p.name} · {p.count}</option>)}
          </select>
          <button onClick={previewDO} disabled={doBusy} data-testid="do-preview-btn"
            style={{ padding: "9px 18px", background: C.navy, color: "#fff", border: 0, borderRadius: 999, fontSize: 13, fontWeight: 600, cursor: doBusy ? "default" : "pointer", opacity: doBusy ? 0.6 : 1 }}>
            {doBusy ? "Checking…" : "Preview audience"}</button>
        </div>
        {doPrev && (
          <div style={{ background: C.cream, borderRadius: 8, padding: 14 }} data-testid="do-preview">
            <div style={{ display: "flex", gap: 20, flexWrap: "wrap", marginBottom: 10 }}>
              {[["Audience", doPrev.audience], ["Will email", doPrev.will_email], ["App prompts", doPrev.will_banner],
                ["Indwe (no app) skipped", doPrev.skipped_indwe_no_app], ["No email & no app", doPrev.skipped_no_email]].map(([l, n]) => (
                <div key={l}><div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.08em", color: C.slate, fontFamily: "'JetBrains Mono', monospace" }}>{l}</div>
                  <div style={{ fontSize: 22, fontWeight: 800, color: C.ink, fontVariantNumeric: "tabular-nums" }}>{(n ?? 0).toLocaleString()}</div></div>
              ))}
            </div>
            {doPrev.dry_run ? (
              <button onClick={sendDO} disabled={doBusy || !((doPrev.will_email || 0) + (doPrev.will_banner || 0))} data-testid="do-send-btn"
                style={{ padding: "10px 20px", background: C.red, color: "#fff", border: 0, borderRadius: 999, fontSize: 13, fontWeight: 700, cursor: "pointer", opacity: (doBusy || !((doPrev.will_email || 0) + (doPrev.will_banner || 0))) ? 0.5 : 1 }}>
                Send requests now</button>
            ) : (
              <div style={{ fontSize: 13, color: C.green, fontWeight: 700 }} data-testid="do-sent">✓ Issued — {doPrev.emailed || 0} emailed, {doPrev.bannered || 0} app prompts set. Track progress on the Subscribers list.</div>
            )}
          </div>
        )}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1.2fr) minmax(0,1fr)", gap: 20 }}>
        {/* Builder */}
        <div style={{ background: "#fff", border: "1px solid rgba(26,30,36,0.08)", borderRadius: 12, padding: 22 }}>
          <div style={{ fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", color: C.slate, fontFamily: "monospace", marginBottom: 14 }}>Segment builder</div>

          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 16 }}>
            {PRESETS.map((p) => (
              <button key={p.label} onClick={() => setF(p.f)} data-testid={`preset-${p.label.split(" ")[0].toLowerCase()}`}
                style={{ display: "flex", alignItems: "center", gap: 6, padding: "7px 12px", background: `${C.navy}0d`, color: C.navy, border: `1px solid ${C.navy}22`, borderRadius: 999, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
                <Lightning size={13} weight="fill" /> {p.label}
              </button>
            ))}
          </div>

          <div style={{ display: "grid", gap: 14 }}>
            <Row label="Product"><Sel value={f.product} onChange={(v) => set("product", v)} testid="seg-product" opts={[["", "Any"], ["consumer", "Consumer"], ["commercial", "Commercial"]]} /></Row>
            <Row label="Insurer"><Sel value={f.insurer} onChange={(v) => set("insurer", v)} testid="seg-insurer" opts={[["", "Any"], ...insurers.map((i) => [i, i])]} /></Row>
            <Row label="Has app"><Sel value={f.has_app} onChange={(v) => set("has_app", v)} testid="seg-app" opts={[["", "Any"], ["true", "Yes"], ["false", "No"]]} /></Row>
            <Row label="Activated"><Sel value={f.activated_app} onChange={(v) => set("activated_app", v)} testid="seg-activated" opts={[["", "Any"], ["true", "Yes"], ["false", "No"]]} /></Row>
            <Row label="Member"><Sel value={f.member_type} onChange={(v) => set("member_type", v)} testid="seg-member" opts={[["", "Any"], ["main", "Main"], ["dependant", "Dependant"]]} /></Row>
            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: C.ink, cursor: "pointer" }}>
              <input type="checkbox" checked={f.exclude_opt_out} onChange={(e) => set("exclude_opt_out", e.target.checked)} data-testid="seg-optout" /> Exclude opted-out contacts
            </label>
          </div>

          <div style={{ borderTop: "1px solid rgba(26,30,36,0.08)", marginTop: 18, paddingTop: 18, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Segment name…" data-testid="seg-name"
              style={{ flex: 1, minWidth: 160, padding: "10px 12px", border: "1px solid rgba(26,30,36,0.14)", borderRadius: 8, fontSize: 13, outline: "none" }} />
            <Sel value={type} onChange={setType} testid="seg-type" opts={[["dynamic", "Dynamic"], ["snapshot", "Snapshot"]]} />
            <button onClick={save} data-testid="seg-save"
              style={{ display: "flex", alignItems: "center", gap: 7, padding: "10px 16px", background: C.ink, color: "#fff", border: 0, borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
              <FloppyDisk size={15} /> Save segment
            </button>
          </div>
        </div>

        {/* Live preview */}
        <div>
          <div style={{ background: C.navy, color: "#fff", borderRadius: 12, padding: 24, marginBottom: 16 }} data-testid="seg-preview">
            <div style={{ fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", color: "rgba(255,255,255,0.5)", fontFamily: "monospace" }}>Live recipients</div>
            <div style={{ fontSize: 48, fontWeight: 800, fontVariantNumeric: "tabular-nums", margin: "6px 0 4px" }} data-testid="seg-count">{preview.count.toLocaleString()}</div>
            <div style={{ fontSize: 12, color: "rgba(255,255,255,0.6)" }}>contacts match this segment</div>
            <div style={{ marginTop: 16, borderTop: "1px solid rgba(255,255,255,0.12)", paddingTop: 14, display: "grid", gap: 8 }}>
              {preview.sample.slice(0, 6).map((s, i) => (
                <div key={`${s.full_name}-${i}`} style={{ display: "flex", justifyContent: "space-between", fontSize: 12 }}>
                  <span>{s.full_name}</span>
                  <span style={{ color: "rgba(255,255,255,0.5)", fontFamily: "monospace" }}>{s.insurer}{s.has_app ? " · app" : ""}</span>
                </div>
              ))}
              {preview.count === 0 && <span style={{ fontSize: 12, color: "rgba(255,255,255,0.5)" }}>No contacts match — widen the filters.</span>}
            </div>
          </div>

          <div style={{ background: "#fff", border: "1px solid rgba(26,30,36,0.08)", borderRadius: 12, padding: 18, marginBottom: 16 }} data-testid="mail-panel">
            <div style={{ fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", color: C.slate, fontFamily: "monospace", marginBottom: 12 }}>Bulk mail · approved template</div>
            <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
              <select value={tmplId} onChange={(e) => setTmplId(e.target.value)} data-testid="mail-template-select"
                style={{ flex: 1, padding: "9px 12px", border: "1px solid rgba(26,30,36,0.14)", borderRadius: 8, fontSize: 13, background: "#fff", color: C.ink, cursor: "pointer" }}>
                <option value="">Choose an approved template…</option>
                {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
              <button onClick={sendTest} data-testid="mail-test-send"
                style={{ padding: "9px 14px", background: C.navy, color: "#fff", border: 0, borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap" }}>Send test to me</button>
            </div>
            <div style={{ borderTop: "1px solid rgba(26,30,36,0.08)", marginTop: 4, paddingTop: 12 }} data-testid="broadcast-panel">
              <div style={{ fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", color: C.slate, fontFamily: "monospace", marginBottom: 8 }}>Broadcast to a segment</div>
              <div style={{ display: "flex", gap: 8 }}>
                <select value={bcastSeg} onChange={(e) => { setBcastSeg(e.target.value); setBcastPreview(null); }} data-testid="broadcast-segment-select"
                  style={{ flex: 1, padding: "9px 12px", border: "1px solid rgba(26,30,36,0.14)", borderRadius: 8, fontSize: 13, background: "#fff", color: C.ink }}>
                  <option value="">Choose a saved segment…</option>
                  {segments.map((s) => <option key={s.id} value={s.id}>{s.name} ({s.count})</option>)}
                </select>
                <button onClick={previewBroadcast} data-testid="broadcast-preview-btn"
                  style={{ padding: "9px 14px", background: C.ink, color: "#fff", border: 0, borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap" }}>Preview recipients</button>
              </div>
              <label style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10, fontSize: 12, color: C.slate, cursor: "pointer" }}>
                <input type="checkbox" checked={groupByPolicy} onChange={(e) => { setGroupByPolicy(e.target.checked); setBcastPreview(null); }} data-testid="broadcast-group-policy" />
                One email per household (group by policy — sends to the main member)
              </label>
              {bcastPreview && (
                <div style={{ marginTop: 10, background: C.cream, borderRadius: 8, padding: 12 }} data-testid="broadcast-preview">
                  <div style={{ fontSize: 13, color: C.ink }}><b>{bcastPreview.recipients.toLocaleString()}</b> recipients with an email{bcastPreview.prod_email_enabled ? "" : " · live send OFF (allowlist only)"}</div>
                  {bcastPreview.grouped_by_policy && (
                    <div style={{ fontSize: 12, color: C.slate, marginTop: 4 }} data-testid="broadcast-grouped-note">Grouped from {bcastPreview.before_grouping.toLocaleString()} members into {bcastPreview.recipients.toLocaleString()} household emails</div>
                  )}
                  <button onClick={sendBroadcast} disabled={bcastPreview.recipients === 0} data-testid="broadcast-send-btn"
                    style={{ marginTop: 10, padding: "9px 16px", background: C.red, color: "#fff", border: 0, borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: bcastPreview.recipients === 0 ? "default" : "pointer", opacity: bcastPreview.recipients === 0 ? 0.5 : 1 }}>Send broadcast</button>
                </div>
              )}
            </div>
            {tmplId && (() => { const t = templates.find((x) => x.id === tmplId); if (!t) return null;
              return edit && edit.id === t.id ? (
                <div style={{ background: C.cream, borderRadius: 8, padding: 12, marginBottom: 10, display: "grid", gap: 8 }} data-testid="mail-edit-panel">
                  <input value={edit.subject} onChange={(e) => setEdit({ ...edit, subject: e.target.value })} data-testid="mail-edit-subject"
                    style={{ padding: "9px 12px", border: "1px solid rgba(26,30,36,0.14)", borderRadius: 8, fontSize: 13, fontWeight: 700 }} />
                  <textarea value={edit.body} onChange={(e) => setEdit({ ...edit, body: e.target.value })} rows={12} data-testid="mail-edit-body"
                    style={{ padding: "9px 12px", border: "1px solid rgba(26,30,36,0.14)", borderRadius: 8, fontSize: 13, fontFamily: "inherit", resize: "vertical", lineHeight: 1.6 }} />
                  <div style={{ fontSize: 11, color: C.slate, lineHeight: 1.5 }}>
                    Blank lines start a new paragraph · lines beginning with • become a bullet list ·
                    placeholders: {"{name}"}{t.body?.includes("{date}") ? ", {date}" : ""}{t.body?.includes("{message}") ? ", {message}" : ""}
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button onClick={() => setEdit(null)} data-testid="mail-edit-cancel"
                      style={{ flex: 1, padding: "10px 14px", background: "transparent", border: `1px solid ${C.slate}44`, color: C.slate, borderRadius: 8, fontSize: 13, cursor: "pointer" }}>Cancel</button>
                    <button onClick={saveEdit} data-testid="mail-edit-save"
                      style={{ flex: 2, padding: "10px 14px", background: C.ink, color: "#fff", border: 0, borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: "pointer" }}>Save wording</button>
                  </div>
                </div>
              ) : (
                <div style={{ background: C.cream, borderRadius: 8, padding: 12, fontSize: 12, color: C.slate, marginBottom: 10 }} data-testid="mail-preview">
                  <div style={{ fontWeight: 700, color: C.ink }}>{t.subject}</div>
                  <div style={{ marginTop: 4, whiteSpace: "pre-wrap" }}>{t.body}</div>
                  <div style={{ display: "flex", gap: 14, marginTop: 10 }}>
                    <button onClick={() => setEdit({ id: t.id, subject: t.subject, body: t.body || "" })} data-testid="mail-edit-open"
                      style={{ background: "none", border: 0, color: C.red, fontSize: 12, fontWeight: 700, cursor: "pointer", padding: 0 }}>Edit wording</button>
                    <button onClick={() => deleteTemplate(t)} data-testid="mail-edit-delete"
                      style={{ background: "none", border: 0, color: C.slate, fontSize: 12, cursor: "pointer", padding: 0 }}>Delete template</button>
                    {t.edited_at && <span style={{ fontSize: 11, color: C.slate }}>edited {String(t.edited_at).slice(0, 16).replace("T", " ")}{t.edited_by ? ` by ${t.edited_by}` : ""}</span>}
                  </div>
                </div>
              ); })()}
            <button onClick={() => setShowNew(!showNew)} data-testid="mail-new-toggle" style={{ background: "none", border: 0, color: C.red, fontSize: 12, fontWeight: 600, cursor: "pointer", padding: 0 }}>{showNew ? "− Cancel" : "+ Save a new template"}</button>
            {showNew && (
              <div style={{ display: "grid", gap: 8, marginTop: 10 }}>
                <input value={nt.name} onChange={(e) => setNt({ ...nt, name: e.target.value })} placeholder="Template name" data-testid="mail-new-name" style={{ padding: "9px 12px", border: "1px solid rgba(26,30,36,0.14)", borderRadius: 8, fontSize: 13 }} />
                <input value={nt.subject} onChange={(e) => setNt({ ...nt, subject: e.target.value })} placeholder="Subject" data-testid="mail-new-subject" style={{ padding: "9px 12px", border: "1px solid rgba(26,30,36,0.14)", borderRadius: 8, fontSize: 13 }} />
                <textarea value={nt.body} onChange={(e) => setNt({ ...nt, body: e.target.value })} placeholder="Body — use {name} and {message}" rows={4} data-testid="mail-new-body" style={{ padding: "9px 12px", border: "1px solid rgba(26,30,36,0.14)", borderRadius: 8, fontSize: 13, fontFamily: "inherit", resize: "vertical" }} />
                <button onClick={saveTemplate} data-testid="mail-new-save" style={{ padding: "10px 14px", background: C.ink, color: "#fff", border: 0, borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>Save &amp; approve for reuse</button>
              </div>
            )}
          </div>

          <div style={{ fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", color: C.slate, fontFamily: "monospace", marginBottom: 10 }}>Saved segments</div>
          {segments.length === 0 && <div style={{ fontSize: 13, color: C.slate }}>None yet — build one and save it.</div>}
          {segments.map((s) => (
            <div key={s.id} style={{ display: "flex", alignItems: "center", gap: 12, background: "#fff", border: "1px solid rgba(26,30,36,0.08)", borderRadius: 10, padding: 14, marginBottom: 8 }} data-testid={`saved-segment-${s.id}`}>
              <div style={{ width: 36, height: 36, borderRadius: 9, background: `${C.red}12`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><Users size={17} color={C.red} /></div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: C.ink }}>{s.name}</div>
                <div style={{ fontSize: 11, color: C.slate, fontFamily: "monospace" }}>{s.type} · {s.count.toLocaleString()} recipients</div>
              </div>
              <button title="Delete" onClick={() => del(s.id)} data-testid={`segment-delete-${s.id}`}
                style={{ padding: 8, border: "1px solid rgba(26,30,36,0.12)", borderRadius: 8, background: "#fff", color: C.red, cursor: "pointer" }}><Trash size={15} /></button>
            </div>
          ))}
        </div>
      </div>

      {/* Migration broadcast — main members with an active app, household-personalised, gated */}
      <div style={{ background: "#fff", border: `1px solid ${C.red}33`, borderRadius: 12, padding: 22, marginTop: 20 }} data-testid="migration-panel">
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
          <div>
            <div style={{ fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", color: C.red, fontFamily: "monospace", marginBottom: 6 }}>Migration broadcast · staged for go-live</div>
            <div style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: 22, color: C.ink }}>Welcome &amp; migration email</div>
            <div style={{ fontSize: 12.5, color: C.slate, marginTop: 4, maxWidth: 560, lineHeight: 1.5 }}>
              Goes to <b>main members who activated the ResQ1 app on the legacy platform</b> and have an email — the primary migration audience. One email per household, personalised with the family members they cover. Legacy members who never activated the old app are left out of this first wave.
            </div>
          </div>
          <button onClick={loadMigration} disabled={migLoading} data-testid="migration-preflight-btn"
            style={{ padding: "10px 16px", background: C.ink, color: "#fff", border: 0, borderRadius: 999, fontSize: 13, fontWeight: 600, cursor: migLoading ? "default" : "pointer", opacity: migLoading ? 0.6 : 1, whiteSpace: "nowrap" }}>
            {migLoading ? "Running…" : "Run pre-flight"}
          </button>
        </div>

        {migration && (
          <div style={{ marginTop: 18 }} data-testid="migration-preflight">
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 14 }}>
              <MigTile label="Household emails" value={migration.recipients} accent={C.red} />
              <MigTile label="Members covered" value={migration.members_covered} accent={C.green} />
              <MigTile label="No email (skipped)" value={migration.excluded_no_email} />
              <MigTile label="No legacy app (left out)" value={migration.excluded_not_activated} />
              <MigTile label="Opted out" value={migration.opted_out} />
            </div>
            <div style={{ background: migration.prod_email_enabled ? `${C.green}14` : `${C.amber}18`, border: `1px solid ${migration.prod_email_enabled ? C.green : C.amber}55`, borderRadius: 8, padding: "10px 13px", fontSize: 12.5, color: C.ink, marginBottom: 14 }} data-testid="migration-gate">
              {migration.prod_email_enabled
                ? "PROD_EMAIL_ENABLED is ON — sending will reach real members."
                : `PROD_EMAIL_ENABLED is OFF — a send now only reaches the test allowlist (${(migration.allowlist || []).join(", ")}). Flip the gate in Settings to go live.`}
              {migration.template_name ? ` · Template: ${migration.template_name}` : " · No migration template found"}
            </div>
            {migration.sample?.length > 0 && (
              <div style={{ background: C.cream, borderRadius: 8, padding: 12, marginBottom: 14 }} data-testid="migration-sample">
                <div style={{ fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", color: C.slate, fontFamily: "monospace", marginBottom: 8 }}>Sample recipients</div>
                {migration.sample.map((s, i) => (
                  <div key={s.email || s.full_name || i} style={{ display: "flex", justifyContent: "space-between", gap: 12, padding: "5px 0", borderTop: i ? `1px solid ${C.ink}0d` : "none" }}>
                    <span style={{ fontSize: 13, color: C.ink, fontWeight: 600 }}>{s.full_name}</span>
                    <span style={{ fontSize: 12, color: C.slate, textAlign: "right" }}>{s.email}{s.covered_names?.length ? ` · covers ${s.covered_names.join(", ")}` : ""}</span>
                  </div>
                ))}
              </div>
            )}
            <div style={{ background: C.cream, borderRadius: 8, padding: 12, marginBottom: 12 }} data-testid="migration-waves">
              <div style={{ fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", color: C.slate, fontFamily: "monospace", marginBottom: 8 }}>Staged release</div>
              <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                <input value={waveEmails} onChange={(e) => setWaveEmails(e.target.value)} data-testid="wave-emails-input"
                  placeholder="first-wave addresses, comma separated"
                  style={{ flex: "1 1 320px", minWidth: 0, padding: "9px 11px", border: "1px solid rgba(26,30,36,0.16)", borderRadius: 8, fontSize: 12.5, outline: "none", background: "#fff" }} />
                <button onClick={() => sendMigration({ only_emails: waveEmails.split(",").map((x) => x.trim()).filter(Boolean) })}
                  disabled={migSending || !waveEmails.trim() || !migration.template_id} data-testid="wave-send-first"
                  style={{ padding: "10px 16px", background: C.ink, color: "#fff", border: 0, borderRadius: 999, fontSize: 12.5, fontWeight: 700, cursor: "pointer", opacity: migSending || !waveEmails.trim() ? 0.5 : 1, whiteSpace: "nowrap" }}>
                  Send first wave
                </button>
                <button onClick={() => sendMigration({ preview_to: waveEmails.split(",")[0].trim() })}
                  disabled={migSending || !waveEmails.trim() || !migration.template_id} data-testid="wave-preview"
                  style={{ padding: "10px 16px", background: "transparent", color: C.ink, border: `1.5px solid ${C.ink}33`, borderRadius: 999, fontSize: 12.5, fontWeight: 700, cursor: "pointer", opacity: migSending || !waveEmails.trim() ? 0.5 : 1, whiteSpace: "nowrap" }}>
                  Email me a preview
                </button>
              </div>
              <div style={{ fontSize: 11.5, color: C.slate, marginTop: 7, lineHeight: 1.5 }}>
                Only these addresses receive the email, each personalised with their own household. Once you're happy, release the rest — anyone already sent is skipped automatically.
              </div>
            </div>
            {autoChase && (
              <div style={{ display: "flex", alignItems: "center", gap: 10, background: autoChase.enabled ? `${C.green}0f` : "#fff", border: `1px solid ${autoChase.enabled ? C.green : C.ink}33`, borderRadius: 10, padding: "10px 13px", marginBottom: 14 }} data-testid="auto-chase-panel">
                <div style={{ flex: 1, fontSize: 12.5, color: C.ink, lineHeight: 1.5 }}>
                  <b data-testid="auto-chase-state">Auto-chase is {autoChase.enabled ? "on" : "off"}.</b>{" "}
                  {autoChase.enabled
                    ? `Anyone who hasn't come back is reminded automatically ${(autoChase.days || []).join(" and ")} days after each wave.`
                    : "Nothing goes out on its own — the wave tracker below tells you when a chaser is due and you press the button."}
                </div>
                <button onClick={toggleAutoChase} data-testid="auto-chase-toggle"
                  style={{ padding: "8px 14px", background: autoChase.enabled ? "transparent" : C.green, color: autoChase.enabled ? C.slate : "#fff", border: autoChase.enabled ? `1px solid ${C.slate}44` : 0, borderRadius: 999, fontSize: 12, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap" }}>
                  {autoChase.enabled ? "Turn off" : "Turn on"}
                </button>
              </div>
            )}
            {waves.length > 0 && (
              <div style={{ marginBottom: 14 }} data-testid="wave-tracker">
                <div style={{ fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", color: C.slate, fontFamily: "monospace", marginBottom: 8 }}>
                  Wave tracker · who opened, activated and hasn't come back
                </div>
                {waves.map((w, wi) => {
                  const f2 = w.funnel || {};
                  const pct = (n) => (f2.sent ? Math.round((n / f2.sent) * 100) : 0);
                  return (
                    <div key={w.id} data-testid={`wave-${wi}`} style={{ background: "#fff", border: "1px solid rgba(26,30,36,0.1)", borderRadius: 10, padding: 14, marginBottom: 8 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", marginBottom: 10 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: C.ink, textTransform: "capitalize" }}>
                          {w.wave || "wave"} wave
                          <span style={{ fontWeight: 400, color: C.slate }}> · {w.created_at ? String(w.created_at).slice(0, 16).replace("T", " ") : ""}</span>
                        </div>
                        <div style={{ fontSize: 11.5, color: C.slate, fontFamily: "monospace" }}>
                          {(w.counts?.sent || 0)} sent{w.counts?.failed ? ` · ${w.counts.failed} failed` : ""}{w.counts?.blocked ? ` · ${w.counts.blocked} blocked` : ""}
                          {w.delivery_confirmed ? ` · ${w.delivery_confirmed} confirmed by Resend` : ""}
                        </div>
                      </div>
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
                        {[["Sent", f2.sent || 0, C.navy], ["Opened", f2.opened || 0, C.amber], ["Activated", f2.activated || 0, C.green], ["Never opened", f2.stalled || 0, C.red]].map(([l, n, col]) => (
                          <div key={l} data-testid={`wave-${wi}-${String(l).toLowerCase()}`} style={{ flex: "1 1 90px", background: `${col}0f`, border: `1px solid ${col}33`, borderRadius: 8, padding: "9px 11px" }}>
                            <div style={{ fontSize: 9.5, letterSpacing: "0.12em", textTransform: "uppercase", color: C.slate, fontFamily: "monospace" }}>{l}</div>
                            <div style={{ fontSize: 19, fontWeight: 700, color: col, fontVariantNumeric: "tabular-nums" }}>
                              {n}<span style={{ fontSize: 11, fontWeight: 500, color: C.slate, marginLeft: 4 }}>{l === "Sent" ? "" : `${pct(n)}%`}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                      {(w.stalled || []).length > 0 && (
                        <details data-testid={`wave-${wi}-stalled-list`}>
                          <summary style={{ fontSize: 12, color: C.red, cursor: "pointer", fontWeight: 600 }} data-testid={`wave-${wi}-chase-summary`}>
                            {w.stalled.length} haven't come back yet
                            {w.chase_due
                              ? " — a chaser is due now"
                              : w.chase_due_in_days > 0 ? ` — chaser due in ${w.chase_due_in_days} day${w.chase_due_in_days === 1 ? "" : "s"}` : ""}
                          </summary>
                          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", margin: "10px 0 4px" }}>
                            <button onClick={() => chaseWave(w)} disabled={migSending || !w.chaseable} data-testid={`wave-${wi}-chase-btn`}
                              style={{ padding: "9px 15px", background: C.red, color: "#fff", border: 0, borderRadius: 999, fontSize: 12.5, fontWeight: 700, cursor: migSending || !w.chaseable ? "default" : "pointer", opacity: migSending || !w.chaseable ? 0.45 : 1 }}>
                              Chase the {w.chaseable} who haven't come back
                            </button>
                            {!w.chase_due && w.chase_due_in_days > 0 && (
                              <span style={{ fontSize: 11.5, color: C.slate }} data-testid={`wave-${wi}-chase-wait`}>
                                Sent {w.days_since_last_contact} days ago — give them {w.chase_due_in_days} more day{w.chase_due_in_days === 1 ? "" : "s"} before nudging again
                              </span>
                            )}
                            {w.stalled.length - w.chaseable > 0 && (
                              <span style={{ fontSize: 11.5, color: C.slate }} data-testid={`wave-${wi}-no-email`}>
                                {w.stalled.length - w.chaseable} have no email — share their link by hand from Subscribers
                              </span>
                            )}
                            {w.chase_rounds > 0 && (
                              <span style={{ fontSize: 11.5, color: C.slate }} data-testid={`wave-${wi}-chase-rounds`}>
                                Chased {w.chase_rounds}×{w.last_chased_at ? ` · last ${String(w.last_chased_at).slice(0, 10)}` : ""}
                              </span>
                            )}
                            {w.undeliverable > 0 && (
                              <span style={{ fontSize: 11.5, color: C.red, fontWeight: 600 }} data-testid={`wave-${wi}-undeliverable`}>
                                {w.undeliverable} dead email address{w.undeliverable === 1 ? "" : "es"} — skipped, phone them instead
                              </span>
                            )}
                          </div>
                          <div style={{ marginTop: 4, maxHeight: 200, overflowY: "auto" }}>
                            {w.stalled.map((p, i) => (
                              <div key={p.id} style={{ display: "flex", justifyContent: "space-between", gap: 10, padding: "5px 0", borderTop: i ? `1px solid ${C.ink}0d` : "none" }}>
                                <span style={{ fontSize: 12.5, color: C.ink, fontWeight: 600 }}>
                                  {p.full_name}
                                  {p.undeliverable ? <span style={{ fontWeight: 700, color: C.red }}> · email bounced</span> : null}
                                  {p.state === "opened" ? <span style={{ fontWeight: 400, color: C.amber }}> · opened, never finished</span> : null}
                                  {p.opted_out ? <span style={{ fontWeight: 700, color: C.slate }}> · opted out</span> : null}
                                  {p.chase_count ? <span style={{ fontWeight: 400, color: C.amber }}> · chased {p.chase_count}×</span> : null}
                                </span>
                                <span style={{ fontSize: 11.5, color: C.slate, fontFamily: "monospace" }}>{[p.email || "no email", p.cell].filter(Boolean).join(" · ")}</span>
                              </div>
                            ))}
                          </div>
                        </details>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            {audit && (
              <div style={{ marginBottom: 14 }} data-testid="migration-audit">
                <div style={{ fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", color: C.slate, fontFamily: "monospace", marginBottom: 8 }}>
                  Data pre-flight · who can't activate cleanly
                </div>
                {audit.blockers.every((b) => b.count === 0) && (
                  <div style={{ background: `${C.green}14`, border: `1px solid ${C.green}44`, borderRadius: 8, padding: "10px 13px", fontSize: 12.5, color: C.ink }}>
                    No data blockers found — every member in the audience can activate.
                  </div>
                )}
                {audit.blockers.filter((b) => b.count > 0).map((b) => (
                  <div key={b.key} data-testid={`audit-${b.key}`} style={{ border: `1px solid ${C.amber}55`, background: `${C.amber}0f`, borderRadius: 8, padding: "11px 13px", marginBottom: 8 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
                      <span style={{ fontSize: 13, fontWeight: 700, color: C.ink }}>{b.label}</span>
                      <span style={{ fontSize: 13, fontWeight: 700, color: C.amber, fontFamily: "monospace" }} data-testid={`audit-count-${b.key}`}>{b.count}</span>
                    </div>
                    <div style={{ fontSize: 12, color: C.slate, marginTop: 3, lineHeight: 1.5 }}>{b.why}</div>
                    {b.sample.map((s, i) => (
                      <FixRow key={s.id || i} row={s} fixable={b.fixable} onSaved={loadMigration} />
                    ))}
                    {b.count > b.sample.length && <div style={{ fontSize: 11, color: C.slate, paddingTop: 5 }}>…and {b.count - b.sample.length} more</div>}
                  </div>
                ))}
              </div>
            )}
            <button onClick={() => sendMigration({ exclude_previous: true })} disabled={migSending || migration.recipients === 0 || !migration.template_id} data-testid="migration-send-btn"
              style={{ padding: "11px 18px", background: C.red, color: "#fff", border: 0, borderRadius: 999, fontSize: 13, fontWeight: 700, cursor: (migSending || migration.recipients === 0) ? "default" : "pointer", opacity: (migSending || migration.recipients === 0 || !migration.template_id) ? 0.5 : 1 }}>
              {migSending ? "Sending…" : migration.prod_email_enabled ? "Release to everyone else" : "Send test (allowlist only)"}
            </button>
          </div>
        )}
      </div>

      <EmailReport />
    </div>
  );
}

const EmailReport = () => {
  const [rep, setRep] = useState(null);
  const [open, setOpen] = useState(null);
  const [people, setPeople] = useState(null);

  useEffect(() => { api.get("/diagnostics/email-report").then((r) => setRep(r.data)).catch(() => {}); }, []);

  const openCampaign = async (subject) => {
    if (open === subject) { setOpen(null); return; }
    setOpen(subject); setPeople(null);
    try {
      const { data } = await api.get("/diagnostics/email-report/recipients", { params: { subject } });
      setPeople(data);
    } catch (e) { toast.error("Couldn't load recipients"); }
  };

  if (!rep) return null;
  const t = rep.totals || {};
  return (
    <div style={{ background: "#fff", border: "1px solid rgba(26,30,36,0.1)", borderRadius: 14, padding: 20, marginTop: 20 }} data-testid="email-report">
      <div style={{ fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", color: C.slate, fontFamily: "monospace" }}>Delivery &amp; engagement</div>
      <div style={{ fontSize: 13, color: C.slate, margin: "6px 0 14px", lineHeight: 1.6 }}>
        Every email we send, as reported back by Resend — what left, what landed, who opened it and who
        clicked. Click a campaign to see the individual people.
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 14 }}>
        {[["Sent", t.sent, null], ["Delivered", t.delivered, C.green], ["Opened", t.opened, C.navy],
          ["Clicked", t.clicked, C.navy], ["Bounced", t.bounced, C.red],
          ["Dead addresses", rep.undeliverable_subscribers, C.red]].map(([l, v, a]) => (
          <MigTile key={l} label={l} value={v} accent={a} />
        ))}
      </div>

      {rep.tracking_hint && (
        <div style={{ background: `${C.amber}0f`, border: `1px solid ${C.amber}55`, borderRadius: 8, padding: "10px 13px", fontSize: 12.5, color: C.ink, lineHeight: 1.6, marginBottom: 14 }} data-testid="email-report-hint">
          {rep.tracking_hint}
        </div>
      )}

      {(rep.campaigns || []).length === 0 ? (
        <div style={{ fontSize: 13, color: C.slate }} data-testid="email-report-empty">
          Nothing reported yet. Delivery events arrive from the Resend webhook the moment you send.
        </div>
      ) : (rep.campaigns || []).map((c) => (
        <div key={c.subject} style={{ borderTop: "1px solid rgba(26,30,36,0.08)", padding: "11px 0" }} data-testid="email-report-campaign">
          <button onClick={() => openCampaign(c.subject)} data-testid={`email-report-open-${c.subject.slice(0, 20)}`}
            style={{ width: "100%", textAlign: "left", background: "none", border: 0, padding: 0, cursor: "pointer" }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "baseline" }}>
              <span style={{ fontSize: 13.5, fontWeight: 700, color: C.ink }}>{c.subject}</span>
              <span style={{ fontSize: 11.5, color: C.slate, fontFamily: "monospace", whiteSpace: "nowrap" }}>{(c.last_at || "").slice(0, 10)}</span>
            </div>
            <div style={{ fontSize: 12, color: C.slate, marginTop: 4, fontFamily: "monospace" }}>
              {c.recipients} people · {c.sent} sent · {c.delivered} delivered · {c.opened} opened ({c.open_rate}%) ·
              {" "}{c.clicked} clicked ({c.click_rate}%){c.bounced ? ` · ${c.bounced} bounced` : ""}
            </div>
          </button>
          {open === c.subject && (
            <div style={{ marginTop: 10, background: C.body, borderRadius: 8, padding: 10, maxHeight: 260, overflowY: "auto" }} data-testid="email-report-recipients">
              {!people ? <div style={{ fontSize: 12, color: C.slate }}>Loading…</div> : (
                <>
                  <div style={{ fontSize: 11.5, color: C.slate, marginBottom: 8 }}>
                    {people.acted} of {people.total} have since activated the app.
                  </div>
                  {people.items.map((p) => (
                    <div key={p.email} style={{ display: "flex", justifyContent: "space-between", gap: 10, padding: "4px 0", fontSize: 12 }}>
                      <span style={{ color: C.ink, fontWeight: 600, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>{p.name || p.email}</span>
                      <span style={{ color: p.status === "bounced" || p.status === "complained" ? C.red : p.status === "clicked" || p.status === "opened" ? C.green : C.slate, fontFamily: "monospace", fontSize: 11, whiteSpace: "nowrap" }}>
                        {p.events.join(" → ")}{p.acted ? " · app active" : ""}
                      </span>
                    </div>
                  ))}
                </>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

const FixRow = ({ row, fixable, onSaved }) => {
  const [val, setVal] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    const email = val.trim();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) return toast.error("Enter a valid email address");
    setBusy(true);
    try {
      await api.put(`/contacts/${row.id}`, { email });
      toast.success(`${row.name} updated`);
      onSaved();
    } catch (e) { toast.error("Couldn't save", { description: e.response?.data?.detail || e.message }); }
    finally { setBusy(false); }
  };
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, paddingTop: 6 }} data-testid={`audit-row-${row.id || row.name}`}>
      <span style={{ fontSize: 11.5, fontWeight: 600, color: C.ink, flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{row.name}</span>
      <span style={{ fontSize: 11, color: C.slate, fontFamily: "monospace", whiteSpace: "nowrap" }}>{row.detail}</span>
      {fixable && row.id && (
        <>
          <input value={val} onChange={(e) => setVal(e.target.value)} placeholder="their own email" data-testid={`audit-input-${row.id}`}
            style={{ width: 200, padding: "6px 9px", border: "1px solid rgba(26,30,36,0.16)", borderRadius: 6, fontSize: 11.5, outline: "none", background: "#fff" }} />
          <button onClick={save} disabled={busy || !val.trim()} data-testid={`audit-save-${row.id}`}
            style={{ padding: "6px 12px", background: C.ink, color: "#fff", border: 0, borderRadius: 6, fontSize: 11.5, fontWeight: 700, cursor: busy || !val.trim() ? "default" : "pointer", opacity: busy || !val.trim() ? 0.45 : 1 }}>
            {busy ? "…" : "Save"}
          </button>
        </>
      )}
    </div>
  );
};

const MigTile = ({ label, value, accent }) => (
  <div style={{ background: "#fff", border: "1px solid rgba(26,30,36,0.1)", borderRadius: 10, padding: "12px 16px", minWidth: 130 }}>
    <div style={{ fontSize: 10, letterSpacing: "0.12em", textTransform: "uppercase", color: "#5B6B7E", fontFamily: "monospace" }}>{label}</div>
    <div style={{ marginTop: 5, fontSize: 24, fontWeight: 700, color: accent || "#1A1E24", fontVariantNumeric: "tabular-nums" }}>{(value ?? 0).toLocaleString()}</div>
  </div>
);

const Row = ({ label, children }) => (
  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 14 }}>
    <span style={{ fontSize: 13, color: "#5B6B7E" }}>{label}</span>{children}
  </div>
);
const Sel = ({ value, onChange, opts, testid }) => (
  <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
    style={{ padding: "8px 12px", border: "1px solid rgba(26,30,36,0.14)", borderRadius: 8, fontSize: 13, background: "#fff", color: "#1A1E24", cursor: "pointer", minWidth: 150 }}>
    {opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
  </select>
);
